/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Box, Container, Typography, makeStyles } from "@material-ui/core";
import {
  FilterButton,
  HeaderWithButtons,
} from "../components-molecules";
import CssBaseline from "@material-ui/core/CssBaseline";
import { theme } from "../constants/theme";
import { useDispatch, useSelector } from "react-redux";
import { CircularProggressIndicator } from "../components-atoms/CircularProggressIndicator";
import multiDownload from "multi-download";
import { InvoiceMerchant } from "../redux/reducers/getInvoiceMerchants";
import { IFilter, IFilterOption } from "../components-molecules/FilterButton";
import { getInvoiceMerchants } from "../redux/actions/getInvoiceMerchants";
import InfiniteScroll from "react-infinite-scroll-component";
import { NoResultsFound } from "../components-molecules/NoResultsFound";
import { AgencyFeeButtonSection } from "../components-molecules/AgencyFeeButtonSection";
import AgencyFeeExportModal from "../components-molecules/AgencyFeeExportModal";
import { exportCSV, getWeeklySummary } from "../redux/actions/getWeeklySummary";
import { WeeklySummaryItem } from "../components-molecules/WeeklySummaryItem";
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { ButtonPrimary } from "../components-atoms";
import { GetTemporaryAgencyFeeWeeklyExportUrl } from "../service/temporary-access-service";
import { useTranslation } from 'react-i18next';

export type OrderSortType = "asc" | "desc";
interface Filter extends IFilter {
  Date: Array<Date | null>;
  Merchants?: string[];
}

const pageSize = 10;

const AgencyFeeSummary = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.login);
  const isAdmin = useMemo(() => userData.role === "Admin", [userData]);

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedSummaries, setSelectedSummaries] = useState<string[]>([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const [invoiceMerchants]: [
    InvoiceMerchant[],
  ] = useSelector((state: any) => [
    state.getInvoiceMerchants.data,
  ]);
  const [filter, setFilter] = useState<Filter>(isAdmin ? {
    Date: [],
    Merchants: [],
  } : { Date: [] });
  const [filterOptions, setFilterOptions] = useState<IFilterOption>(isAdmin ? {
    Date: [
      { title: "From", value: "" },
      { title: "To", value: "" },
    ],
    Merchants: [],
  } : {
    Date: [
      { title: "From", value: "" },
      { title: "To", value: "" },
    ],
  });
  const [filterApplied, setFilterApplied] = useState(filtersApplied(filter));

  useLayoutEffect(() => {
    if (isAdmin) {
      dispatch(getInvoiceMerchants());
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAdmin) {
      const merchantOptions = invoiceMerchants.map((m) => ({
        title: m.Name,
        value: m.Id,
      }));
      setFilterOptions((prev) => ({
        ...prev,
        Merchants: merchantOptions,
      }));
    }

  }, [dispatch, isAdmin, invoiceMerchants]);

  useEffect(() => {
    setFilterApplied(filtersApplied(filter));
  }, [filter]);

  const getSummaries = useCallback(
    (
      size = pageSize,
      currentPage = 0,
      prevData = []
    ) => {
      setLoading(true);
      getWeeklySummary(
        isAdmin,
        {
          Page: currentPage,
          Size: size,
          Query: '',
          Filter: {
            DateFrom: filter.Date?.[0]
              ? new Date(filter.Date[0] as Date)
              : undefined,
            DateTo: filter.Date?.[1]
              ? new Date(filter.Date[1] as Date)
              : undefined,
            Merchants: filter.Merchants
          },
        },
        (res: any) => {
          let total = prevData.length + 1;
          let temp: any = [...prevData, ...res];

          if (res.length === 0) {
            total--;
          }
          setSummaries(temp);
          setLoading(false);
          setTotal(total);
        },
        (err) => {
          setLoading(false);
        }
      )
    },
    [filter]
  );

  useEffect(() => {
    getSummaries();
  }, [getSummaries]);

  const loadMore = () => {
    getSummaries(pageSize, page + 1, summaries);
    setPage((prev) => prev + 1);
  };

  const filterOnSubmit = (filter) => {
    setFilter(filter as Filter);
  };

  const onCheckboxChange = (data, value) => {
    if (value) {
      setSelectedSummaries((prev) => [...prev, data.FileName]);
    } else {
      setSelectedSummaries((prev) => [
        ...prev.filter((f) => f !== data.FileName),
      ]);
    }
  };

  const downloadPdfs = () => {
    let blobs: Array<any> = [];
    let summaryNames: Array<String> = [];
    const selectedList = summaries.filter((f: any) => selectedSummaries.includes(f.FileName));
    selectedList.forEach((item: any, index: number) => {
      GetTemporaryAgencyFeeWeeklyExportUrl(item.Id, async (result) => {
        const data = await fetch(result, {
          method: "GET",
          mode: "cors",
          headers: { Accept: "form-data" },
        });

        const convertedToBlob = await data.blob();
        const convertedToUrl = await window.URL.createObjectURL(
          new Blob([convertedToBlob])
        );
        blobs.push(convertedToUrl);
        summaryNames.push(item.FileName);
        if (selectedSummaries.length - 1 === index) {
          multiDownload(blobs, {
            rename: ({ url, index, urls }) => summaryNames[index] + ".pdf",
          });
        }
      });
    });
  };

  const selectAllClicked = () => {
    setSelectedSummaries(
      summaries.map((m: any) => m.FileName)
    );
  };
  const clearAllClicked = () => {
    setSelectedSummaries([]);
  };

  const onExport = (modalRes) => {
    let body: any = {
      DateFrom: modalRes.DateFrom,
      DateTo: modalRes.DateTo,
    }

    if (isAdmin) {
      body = {
        ...body,
        MerchantId: modalRes.Merchant.Id === 0 ? null : modalRes.Merchant.Id
      }
    }

    exportCSV(isAdmin, body, async (res: any) => {
      var bytes = new Uint8Array(res.Data);
      var blob = new Blob([bytes], { type: "text/csv" })
      var link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = res.FileName;
      link.click();

      setModalVisible(false);
    }, (err) => {
      setModalVisible(false);
    });
  }

  function renderModal() {
    return (
      <AgencyFeeExportModal
        visible={modalVisible}
        isAdmin={isAdmin}
        merchants={invoiceMerchants}
        onExport={onExport}
        onClose={() => {
          setModalVisible(false);
        }} />
    )
  }

  function renderFilters() {
    return (
      <>
        <HeaderWithButtons title={t('merchant.agencyFeeWeeklySummary')} titleXs={8}>
          <HeaderWithButtons.ButtonArea>
            <FilterButton
              dateKey="Date"
              singleDate
              defaultOption="Date"
              data-automation="filter-button"
              onSubmit={filterOnSubmit}
              inButtonSet
              options={filterOptions}
              filter={filter}
            />

            <ButtonPrimary
              className={classes.downloadButton}
              onClick={() => {
                setModalVisible(true);
              }}>
              <OpenInNewIcon className={classes.buttonIcon} />

              <Typography
                variant="button"
                component="span">
{t('merchant.exportCsv')}
              </Typography>
            </ButtonPrimary>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
      </>
    )
  }

  return (
    <Box className={classes.container}>
      <CssBaseline />
      {renderModal()}
      <Container className={classes.root}>
        <CssBaseline />
        {renderFilters()}
        {loading && page === 0 ? (
          <CircularProggressIndicator />
        ) : (
          <div>
            {summaries.length !== 0 ? (
              <AgencyFeeButtonSection
                downloadClicked={downloadPdfs}
                selectAllClicked={selectAllClicked}
                clearAllClicked={clearAllClicked}
                downloadDisabled={selectedSummaries.length === 0}
                clearAllDisabled={selectedSummaries.length === 0}
              />
            ) : null}
            <div>
              {summaries.length !== 0 ? (
                <InfiniteScroll
                  style={{ overflow: "hidden" }}
                  dataLength={summaries.length}
                  next={() => loadMore()}
                  scrollThreshold={0.8}
                  hasMore={
                    summaries.length !== total
                  }
                  loader={<CircularProggressIndicator />} >
                  {summaries.map((item: any, index: number) => {
                    return (
                      <WeeklySummaryItem
                        key={index}
                        data={item}
                        onCheckboxChange={onCheckboxChange}
                        isSelected={selectedSummaries.includes(item.FileName)} />
                    );
                  })}
                </InfiniteScroll>
              ) : (
                <NoResultsFound
                  query={""}
                  filterApplied={filterApplied}
                />
              )}
            </div>
          </div>
        )}
      </Container>
    </Box>
  );
};

const filtersApplied = (filter: any) => {
  let applied = false;

  Object.values(filter).forEach((x: any) => {
    if (x.length > 0) {
      applied = true;
    }
  });
  return applied;
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    flexGrow: 1,
    maxWidth: 840,
    paddingLeft: 0,
    paddingRight: 0,
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
    alignSelf: "center",
  },
  buttonIcon: {
    width: "18px",
    height: "18px",
    marginRight: 8,
  },
  downloadButton: {
    minWidth: 64,
    height: 36,
    borderRadius: 8,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingRight: "16px",
    paddingLeft: "12px",
    marginLeft: 8,
    marginRight: 24,
  },
  textButton: {
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
    "&:active": {
      backgroundColor: theme.palette.secondary[24],
    },
  },
  printButtonText: {
    textTransform: "none",
  },
  noSearchResults: {
    display: "flex",
    paddingLeft: 24,
    paddingTop: 40,
    flexDirection: "column",
  },
  noRecordsFound: {
    display: "flex",
    paddingLeft: "24px",
    flexDirection: "column",
    paddingTop: "96px",
  },
});

export default AgencyFeeSummary;
